import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, DollarSign, Users, Star, Filter, Search, Calendar, Target, ArrowUpRight, ArrowDownRight, Eye, MessageCircle, Send, Home, Car, ShieldCheck, Heart, Hammer, Plane, Laptop, Truck, Award, ChevronRight, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../components/Icon';
import { VendorBadgeCollection } from '../../components/VendorBadge';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const decorativeIcons = [
  { Icon: Home,        label: 'Home',        top: '8%',  left: '2%',  size: 32, rotate: '-12deg', opacity: 0.45 },
  { Icon: Car,         label: 'Auto',        top: '28%', left: '1%',  size: 28, rotate: '8deg',   opacity: 0.4 },
  { Icon: ShieldCheck, label: 'Insurance',   top: '50%', left: '2%',  size: 28, rotate: '-8deg',  opacity: 0.4 },
  { Icon: Heart,       label: 'Wedding',     top: '70%', left: '1%',  size: 28, rotate: '14deg',  opacity: 0.4 },
  { Icon: Laptop,      label: 'Electronics', top: '8%',  right: '2%', size: 32, rotate: '10deg',  opacity: 0.4 },
  { Icon: Hammer,      label: 'Services',    top: '28%', right: '1%', size: 28, rotate: '-10deg', opacity: 0.4 },
  { Icon: Plane,       label: 'Travel',      top: '50%', right: '2%', size: 28, rotate: '-13deg', opacity: 0.4 },
  { Icon: Truck,       label: 'Moving',      top: '70%', right: '1%', size: 28, rotate: '-8deg',  opacity: 0.4 },
];

const PIPELINE_STAGES = [
  { id: 'pending',   label: 'New Inquiries',      color: 'bg-blue-100 text-blue-800' },
  { id: 'submitted', label: 'Quotes Submitted',   color: 'bg-yellow-100 text-yellow-800' },
  { id: 'accepted',  label: 'Won',                color: 'bg-green-100 text-green-800' },
  { id: 'rejected',  label: 'Lost',               color: 'bg-red-100 text-red-800' },
];

const VendorHubDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedStage, setSelectedStage] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [kpiData, setKpiData] = useState({
    activeLeads: { value: 0, change: 0, trend: 'up' },
    conversionRate: { value: 0, change: 0, trend: 'up' },
    avgQuoteValue: { value: 0, change: 0, trend: 'up' },
    quoteSuccess: { value: 0, change: 0, trend: 'up' },
  });
  const [pipelineStages, setPipelineStages] = useState(PIPELINE_STAGES?.map(s => ({ ...s, count: 0 })));

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // 1. Load vendor profile for the logged-in user
      const { data: vendorData, error: vendorError } = await supabase?.from('vendors')?.select('*')?.eq('email', user?.email)?.single();

      if (!vendorError && vendorData) {
        setVendorProfile({
          id: vendorData?.id,
          name: vendorData?.name,
          email: vendorData?.email,
          phone: vendorData?.phone,
          website: vendorData?.website,
          businessAddress: vendorData?.company_name,
          rating: vendorData?.rating || 0,
          completedJobs: vendorData?.total_reviews || 0,
          completedProjects: vendorData?.total_reviews || 0,
          certifications: vendorData?.categories || [],
          // Badge-related fields with sensible defaults
          hasIdentityVerification: true,
          hasBusinessLicense: true,
          noRecentDisputes: true,
          quoteAcceptanceRate: 0,
          usesToolsConsistently: true,
          quotesDelivered: 0,
          cancellationRate: 0,
          profileCompletion: 80,
          quotesPerMonth: 0,
          noTosViolations: true,
          engagementScore: 70,
          hasGovernmentId: false,
          hasVerifiedPhone: !!vendorData?.phone,
          hasVerifiedEmail: !!vendorData?.email,
          hasValidatedEin: false,
          hasConfirmedAddress: false,
          hasInsuranceCertificate: false,
          manuallyValidated: false,
          hasManufacturerCertification: false,
          isPaidFeatured: false,
          onTimeRate: 0,
          favoritesSaveRate: 0,
          quoteAccuracyRate: 0,
          leadResponseRate: 0,
          medianResponseTime: 0,
          maxResponseGap: 0,
          complaintRate: 0,
          platformActivityDays: 0,
          completedMiBuyProjects: 0,
        });

        // 2. Load quotes for this vendor
        const { data: quotesData, error: quotesError } = await supabase?.from('quotes')?.select(`
            *,
            projects(id, name, category, description, deadline, location, budget_min, budget_max, status)
          `)?.eq('vendor_id', vendorData?.id)?.order('created_at', { ascending: false });

        if (!quotesError && quotesData) {
          setQuotes(quotesData);
          computeKPIs(quotesData);
          computePipeline(quotesData);
          buildLeads(quotesData);
        }
      } else {
        // Vendor not found — show empty state
        setVendorProfile(null);
      }
    } catch (err) {
      console.error('Error loading vendor dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const computeKPIs = (quotesData) => {
    const total = quotesData?.length;
    const active = quotesData?.filter(q => q?.status === 'pending' || q?.status === 'submitted')?.length;
    const won = quotesData?.filter(q => q?.status === 'accepted')?.length;
    const submitted = quotesData?.filter(q => q?.status === 'submitted' || q?.status === 'accepted' || q?.status === 'rejected')?.length;
    const conversionRate = submitted > 0 ? Math.round((won / submitted) * 100) : 0;
    const winRate = total > 0 ? Math.round((won / total) * 100) : 0;
    const amounts = quotesData?.filter(q => q?.total_amount)?.map(q => parseFloat(q?.total_amount));
    const avgQuote = amounts?.length > 0 ? Math.round(amounts?.reduce((a, b) => a + b, 0) / amounts?.length) : 0;

    setKpiData({
      activeLeads: { value: active, change: 0, trend: 'up' },
      conversionRate: { value: conversionRate, change: 0, trend: conversionRate >= 30 ? 'up' : 'down' },
      avgQuoteValue: { value: avgQuote, change: 0, trend: 'up' },
      quoteSuccess: { value: winRate, change: 0, trend: winRate >= 50 ? 'up' : 'down' },
    });
  };

  const computePipeline = (quotesData) => {
    setPipelineStages(PIPELINE_STAGES?.map(stage => ({
      ...stage,
      count: quotesData?.filter(q => q?.status === stage?.id)?.length,
    })));
  };

  const buildLeads = (quotesData) => {
    const mapped = quotesData?.filter(q => q?.projects)?.map(q => ({
        id: q?.id,
        quoteId: q?.id,
        projectId: q?.project_id,
        clientName: q?.projects?.name || 'Unknown Project',
        projectTitle: q?.projects?.name || 'Untitled Project',
        projectType: q?.projects?.category?.replace(/_/g, ' ') || 'General',
        quoteValue: q?.total_amount ? parseFloat(q?.total_amount) : null,
        stage: q?.status,
        priority: q?.total_amount > 10000 ? 'high' : q?.total_amount > 5000 ? 'medium' : 'low',
        lastActivity: q?.updated_at ? new Date(q.updated_at)?.toLocaleDateString() : 'N/A',
        timeline: q?.projects?.deadline ? `Due ${new Date(q.projects.deadline)?.toLocaleDateString()}` : 'No deadline',
        description: q?.projects?.description || 'No description provided.',
        location: q?.projects?.location || 'Location not specified',
        deadline: q?.projects?.deadline,
        icon: getCategoryIcon(q?.projects?.category),
      }));
    setLeads(mapped);
  };

  const getCategoryIcon = (category) => {
    const map = {
      cars: 'Car',
      home_improvement: 'Home',
      electronics: 'Laptop',
      furniture: 'Layers',
      appliances: 'Zap',
      other: 'Package',
    };
    return map?.[category] || 'Home';
  };

  const filteredLeads = leads?.filter(lead => {
    const matchesStage = selectedStage === 'all' || lead?.stage === selectedStage;
    const matchesSearch =
      lead?.clientName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      lead?.projectTitle?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      lead?.projectType?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesStage && matchesSearch;
  });

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border border-amber-200';
      case 'low': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStageAccent = (stage) => {
    switch (stage) {
      case 'pending':   return 'border-l-blue-400';
      case 'submitted': return 'border-l-amber-400';
      case 'accepted':  return 'border-l-emerald-400';
      case 'rejected':  return 'border-l-red-400';
      default: return 'border-l-gray-300';
    }
  };

  const getStageBadgeColor = (stageId) => {
    return PIPELINE_STAGES?.find(s => s?.id === stageId)?.color || 'bg-gray-100 text-gray-700';
  };

  const getStageLabelById = (stageId) => {
    return PIPELINE_STAGES?.find(s => s?.id === stageId)?.label || stageId;
  };

  const kpiCards = [
    {
      label: 'Active Leads',
      value: kpiData?.activeLeads?.value,
      suffix: '',
      change: kpiData?.activeLeads?.change,
      trend: kpiData?.activeLeads?.trend,
      icon: 'Users',
      gradientFrom: '#F58A54',
      gradientTo: '#F06F6B',
    },
    {
      label: 'Conversion Rate',
      value: kpiData?.conversionRate?.value,
      suffix: '%',
      change: kpiData?.conversionRate?.change,
      trend: kpiData?.conversionRate?.trend,
      icon: 'Target',
      gradientFrom: '#F06F6B',
      gradientTo: '#E0528A',
    },
    {
      label: 'Avg Quote Value',
      value: kpiData?.avgQuoteValue?.value > 0 ? `$${kpiData?.avgQuoteValue?.value?.toLocaleString()}` : '$0',
      suffix: '',
      change: Math.abs(kpiData?.avgQuoteValue?.change),
      trend: kpiData?.avgQuoteValue?.trend,
      icon: 'DollarSign',
      gradientFrom: '#F58A54',
      gradientTo: '#F06F6B',
    },
    {
      label: 'Win Rate',
      value: kpiData?.quoteSuccess?.value,
      suffix: '%',
      change: kpiData?.quoteSuccess?.change,
      trend: kpiData?.quoteSuccess?.trend,
      icon: 'Star',
      gradientFrom: '#E0528A',
      gradientTo: '#F06F6B',
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin mx-auto mb-4" style={{ borderColor: '#F06F6B', borderTopColor: 'transparent' }} />
            <p className="text-gray-500 text-sm">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-sm mx-auto px-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
              <Icon name="Lock" size={28} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in required</h2>
            <p className="text-gray-500 text-sm mb-6">Please sign in to access your vendor dashboard.</p>
            <button onClick={() => navigate('/login-sign-up')} className="px-6 py-2.5 text-white rounded-xl font-semibold" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Gradient Hero Banner */}
      <div
        className="relative pt-32 pb-10 px-4 sm:px-6 lg:px-8 overflow-hidden"
        style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
      >
        {/* Decorative blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute bottom-0 -left-10 w-56 h-56 rounded-full bg-white/5 blur-2xl" />
          {decorativeIcons?.map((item, i) => (
            <div
              key={i}
              className="absolute flex flex-col items-center gap-1"
              style={{
                top: item?.top,
                left: item?.left,
                right: item?.right,
                opacity: item?.opacity,
                transform: `rotate(${item?.rotate})`
              }}
            >
              <div className="w-12 h-12 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center backdrop-blur-sm">
                <item.Icon size={item?.size} color="white" />
              </div>
              <span className="text-white text-xs font-medium tracking-wide">{item?.label}</span>
            </div>
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Badge */}
          <div className="flex mb-4">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="text-white/90 text-sm font-medium tracking-wide">Vendor Hub</span>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Welcome back, {vendorProfile?.name || user?.email?.split('@')?.[0] || 'Vendor'}
              </h1>
              <p className="text-white/80 text-lg">Manage your leads, track your pipeline, and grow your business</p>
            </div>
            {/* Quick stats in hero */}
            <div className="flex gap-4">
              <div className="bg-white/15 backdrop-blur-sm border border-white/20 rounded-2xl px-5 py-3 text-center">
                <p className="text-2xl font-bold text-white">{leads?.length}</p>
                <p className="text-white/75 text-xs font-medium">Total Leads</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm border border-white/20 rounded-2xl px-5 py-3 text-center">
                <p className="text-2xl font-bold text-white">{vendorProfile?.rating > 0 ? `${vendorProfile?.rating}★` : 'N/A'}</p>
                <p className="text-white/75 text-xs font-medium">Rating</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm border border-white/20 rounded-2xl px-5 py-3 text-center">
                <p className="text-2xl font-bold text-white">{kpiData?.quoteSuccess?.value}%</p>
                <p className="text-white/75 text-xs font-medium">Win Rate</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 -mt-6">
          {kpiCards?.map((card, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-md border border-gray-100 p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${card?.gradientFrom}, ${card?.gradientTo})` }}
                >
                  <Icon name={card?.icon} size={18} className="text-white" />
                </div>
                <div className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                  card?.trend === 'up' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                }`}>
                  {card?.trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {card?.change}%
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{card?.value}{card?.suffix}</p>
              <p className="text-sm text-gray-500 mt-0.5">{card?.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Vendor Profile Card */}
          <div className="lg:col-span-1 space-y-5">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Profile header with gradient */}
              <div
                className="h-20 relative"
                style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)' }}
              >
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2">
                  <div
                    className="w-16 h-16 rounded-2xl border-4 border-white flex items-center justify-center shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                  >
                    <Icon name="User" size={28} className="text-white" />
                  </div>
                </div>
              </div>

              <div className="pt-10 pb-5 px-5 text-center">
                <h2 className="text-lg font-bold text-gray-900">{vendorProfile?.name || user?.email}</h2>
                <div className="flex items-center justify-center gap-1 mt-1">
                  <Star size={14} fill="#F58A54" color="#F58A54" />
                  <span className="text-sm font-semibold text-gray-700">{vendorProfile?.rating || '—'}</span>
                  <span className="text-sm text-gray-400">• {vendorProfile?.completedJobs || 0} reviews</span>
                </div>
                {vendorProfile && (
                  <div className="mt-3">
                    <VendorBadgeCollection vendor={vendorProfile} maxBadges={3} size="sm" />
                  </div>
                )}
              </div>

              {/* Profile details */}
              <div className="px-5 pb-5 space-y-2.5 border-t border-gray-50 pt-4">
                {[
                  { icon: 'Mail', label: vendorProfile?.email || user?.email },
                  { icon: 'Phone', label: vendorProfile?.phone || 'Not provided' },
                  { icon: 'Globe', label: vendorProfile?.website || 'Not provided' },
                  { icon: 'MapPin', label: vendorProfile?.businessAddress || 'Not provided' },
                ]?.map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF5F0' }}>
                      <Icon name={item?.icon} size={13} style={{ color: '#F58A54' }} />
                    </div>
                    <span className="text-xs text-gray-600 truncate">{item?.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Badge Progress Card */}
            {vendorProfile && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #F58A54, #E0528A)' }}
                  >
                    <Award size={14} color="white" />
                  </div>
                  Badge Progress
                </h3>
                <div className="space-y-3">
                  {[
                    { label: 'Verified Email', earned: vendorProfile?.hasVerifiedEmail },
                    { label: 'Verified Phone', earned: vendorProfile?.hasVerifiedPhone },
                    { label: 'Business License', earned: vendorProfile?.hasBusinessLicense },
                    { label: 'Identity Verified', earned: vendorProfile?.hasIdentityVerification },
                    { label: 'Featured Vendor', earned: vendorProfile?.isPaidFeatured, upgrade: !vendorProfile?.isPaidFeatured },
                  ]?.map((badge, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className={`text-sm ${badge?.earned ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>{badge?.label}</span>
                      {badge?.earned && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">✓ Earned</span>
                      )}
                      {!badge?.earned && badge?.upgrade && (
                        <button
                          className="text-xs font-semibold px-3 py-1 rounded-lg border transition-colors"
                          style={{ color: '#F06F6B', borderColor: '#F06F6B' }}
                        >
                          Upgrade
                        </button>
                      )}
                      {!badge?.earned && !badge?.upgrade && (
                        <span className="text-xs text-gray-400">Pending</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                {[
                  { icon: 'HelpCircle', label: 'How It Works', sub: 'Vendor guide', from: '#F58A54', to: '#F06F6B', path: '/vendor-how-it-works' },
                  { icon: 'BarChart3', label: 'View Analytics', sub: 'Performance insights', from: '#F06F6B', to: '#E0528A', path: '/vendor-analytics-dashboard' },
                  { icon: 'TrendingUp', label: 'Manage Ads', sub: 'Sponsored listings', from: '#E0528A', to: '#F58A54', path: '/vendor-analytics-advertising' },
                ]?.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => navigate(action?.path)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all text-left"
                  >
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `linear-gradient(135deg, ${action?.from}, ${action?.to})` }}
                    >
                      <Icon name={action?.icon} size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{action?.label}</p>
                      <p className="text-xs text-gray-500">{action?.sub}</p>
                    </div>
                    <ChevronRight size={14} className="text-gray-400 ml-auto" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Pipeline Overview */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-5">Pipeline Overview</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedStage('all')}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                    selectedStage === 'all' ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={selectedStage === 'all' ? { background: 'linear-gradient(135deg, #F58A54, #F06F6B)' } : {}}
                >
                  All ({leads?.length})
                </button>
                {pipelineStages?.map((stage) => (
                  <button
                    key={stage?.id}
                    onClick={() => setSelectedStage(stage?.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                      selectedStage === stage?.id
                        ? 'text-white shadow-sm'
                        : `${stage?.color} hover:opacity-80`
                    }`}
                    style={selectedStage === stage?.id ? { background: 'linear-gradient(135deg, #F58A54, #F06F6B)' } : {}}
                  >
                    {stage?.label} ({stage?.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Search & Filter Bar */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search leads, projects, or clients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#F06F6B' }}
                  />
                </div>
                <button className="flex items-center gap-2 px-4 py-2.5 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium">
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
              </div>
            </div>

            {/* Leads */}
            <div className="space-y-4">
              {filteredLeads?.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                  >
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">No leads yet</h3>
                  <p className="text-gray-500 mb-6 text-sm">
                    {leads?.length === 0
                      ? "You haven't been invited to any projects yet. Complete your profile to get matched with buyers."
                      : "No leads match your current search or filter."}
                  </p>
                  <button
                    onClick={() => navigate('/vendor-how-it-works')}
                    className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold"
                    style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                  >
                    How It Works
                  </button>
                </div>
              ) : (
                filteredLeads?.map((lead) => (
                  <div
                    key={lead?.id}
                    className={`bg-white rounded-2xl shadow-sm border-l-4 ${getStageAccent(lead?.stage)} border border-gray-100 p-5 hover:shadow-md transition-all`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex gap-4 flex-1">
                        {/* Category icon */}
                        <div
                          className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                        >
                          <Icon name={lead?.icon || 'Home'} size={20} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-1">
                            <h3 className="text-base font-bold text-gray-900">{lead?.projectTitle}</h3>
                          </div>
                          <p className="text-sm text-gray-500 mb-2">{lead?.location}</p>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-1">{lead?.description}</p>

                          <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
                            {lead?.quoteValue && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-3.5 h-3.5" />
                                ${lead?.quoteValue?.toLocaleString()}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {lead?.timeline}
                            </span>
                            {lead?.deadline && (
                              <span className="flex items-center gap-1">
                                <Target className="w-3.5 h-3.5" />
                                Due {new Date(lead.deadline)?.toLocaleDateString()}
                              </span>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${getStageBadgeColor(lead?.stage)}`}>
                              {getStageLabelById(lead?.stage)}
                            </span>
                            <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${getPriorityBadge(lead?.priority)}`}>
                              {lead?.priority?.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-400">{lead?.lastActivity}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex lg:flex-col gap-2 lg:items-stretch">
                        <button
                          onClick={() => navigate(`/vendor-project-details/${lead?.projectId}`)}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-colors"
                          style={{ color: '#F58A54', borderColor: '#F58A54', backgroundColor: '#FFF5F0' }}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                        <button
                          onClick={() => navigate('/buyer-vendor-messaging-interface')}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-colors"
                          style={{ color: '#F06F6B', borderColor: '#F06F6B', backgroundColor: '#FFF0F5' }}
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          Chat
                        </button>
                        <button
                          onClick={() => navigate(`/vendor-project-details/${lead?.projectId}`)}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90"
                          style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                        >
                          <Send className="w-3.5 h-3.5" />
                          Quote
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorHubDashboard;